import React, { useEffect, useState } from "react";
import { SubTitle } from "../../Components/CustomComponents";
import Toast from "../../Hooks/useToast";
import { getService, patchService } from "../../redux/services";
import { api_urls } from "../../utilities";
import styles from "./styles/wallet_management.module.scss";

const normalizeTransactions = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  return (
    payload.transactions ??
    payload.data?.transactions ??
    payload.records ??
    payload.list ??
    []
  );
};

const normalizeUsers = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return [];
  return payload.users ?? payload.data?.users ?? payload.list ?? [];
};

const formatAmount = (value) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return "-";
  return `$${amount.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function WalletManagementScreen() {
  const TAB_KEYS = {
    USERS: "users",
    TRANSACTIONS: "transactions",
  };
  const [transactions, setTransactions] = useState([]);
  const [usersWallets, setUsersWallets] = useState([]);
  const [activeTab, setActiveTab] = useState(TAB_KEYS.USERS);
  const [loading, setLoading] = useState(true);
  const [settingsLoading, setSettingsLoading] = useState(true);
  const [walletSummaryLoading, setWalletSummaryLoading] = useState(true);
  const [toggleLoading, setToggleLoading] = useState(false);
  const [reviewBonusEnabled, setReviewBonusEnabled] = useState(false);
  const [accountBalance, setAccountBalance] = useState(null);

  const fetchWalletData = async () => {
    setLoading(true);
    try {
      const [transactionsRes, usersRes] = await Promise.all([
        getService(api_urls.walletTransactions),
        getService(api_urls.walletUsers),
      ]);

      const transactionsEnvelope = transactionsRes?.data ?? {};
      const usersEnvelope = usersRes?.data ?? {};

      setTransactions(normalizeTransactions(transactionsEnvelope?.data ?? transactionsEnvelope));
      setUsersWallets(normalizeUsers(usersEnvelope?.data ?? usersEnvelope));
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Unable to fetch wallet data";
      Toast(msg, "error");
      setTransactions([]);
      setUsersWallets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchWalletSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await getService(api_urls.walletSettings);
      const envelope = response?.data ?? {};
      const data = envelope?.data ?? envelope;
      setReviewBonusEnabled(Boolean(data?.review_bonus_enabled));
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Unable to fetch wallet settings";
      Toast(msg, "error");
      setReviewBonusEnabled(false);
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchWalletSummary = async () => {
    setWalletSummaryLoading(true);
    try {
      const response = await getService(api_urls.wallet);
      const envelope = response?.data ?? {};
      const data = envelope?.data ?? envelope;
      const wallet = data?.wallet ?? {};
      setAccountBalance(wallet?.balance ?? null);
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Unable to fetch account balance";
      Toast(msg, "error");
      setAccountBalance(null);
    } finally {
      setWalletSummaryLoading(false);
    }
  };

  useEffect(() => {
    fetchWalletData();
    fetchWalletSettings();
    fetchWalletSummary();
  }, []);

  const handleToggle = async () => {
    const nextValue = !reviewBonusEnabled;
    setToggleLoading(true);
    try {
      const response = await patchService(api_urls.walletSettings, {
        review_bonus_enabled: nextValue,
      });
      const envelope = response?.data ?? {};
      const data = envelope?.data ?? envelope;
      setReviewBonusEnabled(Boolean(data?.review_bonus_enabled));
      Toast("Review bonus settings updated", "success");
    } catch (error) {
      const msg =
        error?.data?.message ?? error?.message ?? "Unable to update settings";
      Toast(msg, "error");
    } finally {
      setToggleLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <SubTitle title="Wallet Management" />
      </div>

      <div className={styles.topControlsRow}>
        <div className={styles.metaRow}>
          <div className={styles.balanceWrap}>
            <span className={styles.switchLabel}>Account Balance</span>
            <span className={styles.balanceValue}>
              {walletSummaryLoading ? "Loading..." : formatAmount(accountBalance)}
            </span>
          </div>

          <div className={styles.switchWrap}>
            <span className={styles.switchLabel}>Review Bonus</span>
            <button
              type="button"
              className={`${styles.switchButton} ${
                reviewBonusEnabled ? styles.switchOn : styles.switchOff
              }`}
              onClick={handleToggle}
              disabled={settingsLoading || toggleLoading}
              aria-label="Toggle review bonus"
            >
              <span className={styles.switchKnob} />
            </button>
          </div>
        </div>

        <div className={styles.tabBar}>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TAB_KEYS.USERS ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab(TAB_KEYS.USERS)}
          >
            User Wallets
          </button>
          <button
            type="button"
            className={`${styles.tabButton} ${
              activeTab === TAB_KEYS.TRANSACTIONS ? styles.activeTab : ""
            }`}
            onClick={() => setActiveTab(TAB_KEYS.TRANSACTIONS)}
          >
            Transactions
          </button>
        </div>
      </div>

      <section className={styles.section}>
        {activeTab === TAB_KEYS.TRANSACTIONS ? (
          <>
            <h3>Transactions</h3>
            {loading ? (
              <p className={styles.emptyState}>Loading transactions...</p>
            ) : transactions.length === 0 ? (
              <p className={styles.emptyState}>No transactions found</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>User</th>
                      <th>Type</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.map((item) => (
                      <tr key={item.id}>
                        <td>{item.id ?? "-"}</td>
                        <td>{item?.user?.name || item?.user?.email || "-"}</td>
                        <td>{item.type ?? "-"}</td>
                        <td>{formatAmount(item.amount)}</td>
                        <td>{item.description ?? "-"}</td>
                        <td>{formatDate(item.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          <>
            <h3>User Wallets</h3>
            {loading ? (
              <p className={styles.emptyState}>Loading user wallets...</p>
            ) : usersWallets.length === 0 ? (
              <p className={styles.emptyState}>No wallet users found</p>
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Wallet ID</th>
                      <th>User</th>
                      <th>Email</th>
                      <th>Balance</th>
                      <th>Currency</th>
                      <th>Created At</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usersWallets.map((item) => (
                      <tr key={item.wallet_id ?? item.id}>
                        <td>{item.wallet_id ?? item.id ?? "-"}</td>
                        <td>{item?.user?.name ?? "-"}</td>
                        <td>{item?.user?.email ?? "-"}</td>
                        <td>{formatAmount(item.balance)}</td>
                        <td>{item.currency ?? "-"}</td>
                        <td>{formatDate(item.createdAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  );
}
